import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface SensorGroupsFieldProps {
  selected: string[];
  options: Array<{ _id: string; name: string }>;
  onChange: (field: string, value: string[]) => void;
}

export function SensorGroupsField({
  selected,
  options,
  onChange,
}: SensorGroupsFieldProps) {
  const selectedNames = useMemo(() => {
    const map = new Map(options.map((o) => [o._id, o.name]));
    return selected.map((id) => ({ id, name: map.get(id) || id }));
  }, [selected, options]);

  const toggle = (id: string) => {
    const next = selected.includes(id)
      ? selected.filter((s) => s !== id)
      : [...selected, id];
    onChange('sensorgroups', next);
  };

  const remove = (id: string) => {
    onChange('sensorgroups', selected.filter((s) => s !== id));
  };

  return (
    <div className="space-y-2">
      <Label>Sensor Groups</Label>
      <p className="text-xs text-muted-foreground">
        Sensor groups are used for filtering and user visibility. Sensors can be
        assigned to multiple groups.
      </p>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start font-normal"
          >
            {selected.length
              ? `${selected.length} group${selected.length > 1 ? 's' : ''} selected`
              : 'Select groups...'}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64 max-h-56 overflow-y-auto">
          {options.length === 0 ? (
            <p className="text-sm text-muted-foreground px-2 py-1.5">
              No sensor groups available
            </p>
          ) : (
            options.map((group) => (
              <DropdownMenuCheckboxItem
                key={group._id}
                checked={selected.includes(group._id)}
                onCheckedChange={() => toggle(group._id)}
              >
                {group.name}
              </DropdownMenuCheckboxItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {selectedNames.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedNames.map(({ id, name }) => (
            <Badge key={id} variant="secondary" className="gap-1 pr-1">
              {name}
              <button
                type="button"
                onClick={() => remove(id)}
                className="ml-0.5 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
